import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { UserFilters } from "@/components/users/UserFilters";
import { UsersTable } from "@/components/users/UsersTable";
import { CreateEditUserDialog } from "@/components/users/CreateEditUserDialog";
import { DeleteUserDialog } from "@/components/users/DeleteUserDialog";
import {
  User,
  CreateUserData,
  UpdateUserData,
  ApiError,
  fetchUsers,
  createUser,
  updateUser,
  activateUser,
  inactivateUser,
  deleteUser,
} from "@/services/user-service";

export interface UserFiltersData {
  nameLike?: string;
  email?: string;
  pageSize?: number;
}

export default function Users() {
  const { user: loggedUser, can } = useAuth();
  const isSuperAdmin = loggedUser?.role === "super_admin";
  const canUpdate = can("users", "update");
  const canRead = can("users", "read");

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filters, setFilters] = useState<UserFiltersData>({
    nameLike: "",
    email: "",
    pageSize: 20,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const pageSize = filters.pageSize || 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isLastPage = currentPage >= totalPages;

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, includeInactive, currentPage]);

  /** Mostra um toast amigável e converte 403 em mensagem dedicada. */
  const handleApiError = (err: unknown, fallbackTitle: string) => {
    const isForbidden = err instanceof ApiError && err.status === 403;
    toast({
      title: isForbidden ? "Acesso negado" : fallbackTitle,
      description:
        err instanceof Error ? err.message : "Erro desconhecido",
      variant: "destructive",
    });
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchUsers(
        includeInactive,
        false,
        filters.nameLike || undefined,
        filters.email || undefined,
        undefined,
        currentPage,
        pageSize
      );
      setUsers(data.items);
      setTotal(data.total);
    } catch (error) {
      handleApiError(error, "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: UserFiltersData) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setSelectedUser(null);
    setCreateEditDialogOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setCreateEditDialogOpen(true);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleSave = async (
    data: CreateUserData | UpdateUserData,
    userId?: string
  ) => {
    try {
      if (userId) {
        await updateUser(userId, data as UpdateUserData);
        toast({
          title: "Usuário atualizado",
          description: "As informações do usuário foram atualizadas com sucesso.",
        });
      } else {
        await createUser(data as CreateUserData);
        toast({
          title: "Usuário criado",
          description: "O novo usuário foi criado com sucesso.",
        });
      }
      loadUsers();
    } catch (error) {
      handleApiError(
        error,
        userId ? "Erro ao atualizar usuário" : "Erro ao criar usuário"
      );
      throw error; // mantém o diálogo aberto
    }
  };

  const handleConfirmDelete = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast({
        title: "Usuário excluído",
        description: "O usuário foi excluído com sucesso.",
      });
      loadUsers();
    } catch (error) {
      handleApiError(error, "Erro ao excluir usuário");
    }
  };

  const handleActivate = async (user: User) => {
    try {
      await activateUser(user.id);
      toast({
        title: "Usuário ativado",
        description: `${user.name} foi ativado com sucesso.`,
      });
      loadUsers();
    } catch (error) {
      handleApiError(error, "Erro ao ativar usuário");
    }
  };

  const handleInactivate = async (user: User) => {
    try {
      await inactivateUser(user.id);
      toast({
        title: "Usuário inativado",
        description: `${user.name} foi inativado com sucesso.`,
      });
      loadUsers();
    } catch (error) {
      handleApiError(error, "Erro ao inativar usuário");
    }
  };

  // RBAC: define se a ação é permitida sobre um usuário-alvo específico.
  // Regra: super_admin pode tudo; demais só podem mexer em usuários da própria
  // company E de nível abaixo (N+1) do actor — ou seja, nunca em pares/superiores.
  const canManageTarget = (target: User): boolean => {
    if (!canUpdate) return false;
    if (isSuperAdmin) return true;
    if (!loggedUser?.company_id) return false;
    if (target.company_id !== loggedUser.company_id) return false;
    const actorLevel = loggedUser.level;
    const targetLevel = target.level;
    if (actorLevel === undefined || targetLevel === undefined) return true;
    return targetLevel > actorLevel;
  };

  if (!canRead) {
    return (
      <div className="page-wrapper">
        <h1 className="page-title">Usuários</h1>
        <p className="page-subtitle">
          Você não tem permissão para acessar a gestão de usuários.
        </p>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Usuários</h1>
          <p className="page-subtitle">Gerencie os usuários do sistema</p>
        </div>
        <Button size="sm" onClick={handleCreate} className="btn-header">
          <Plus className="btn-header-icon" />
          Novo Usuário
        </Button>
      </div>

      <UserFilters
        onFilterChange={handleFilterChange}
        includeInactive={includeInactive}
        onIncludeInactiveChange={setIncludeInactive}
      />
      <div className="space-y-2">
        <p className="page-counter">
          {total} usuário(s) encontrado(s)
        </p>
        <UsersTable
          users={users}
          loading={loading}
          showCompany={isSuperAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onInactivate={handleInactivate}
          canManageTarget={canManageTarget}
        />
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
          <span className="page-counter">
            Página {currentPage} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="btn-header"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="btn-header"
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={isLastPage || loading}
            >
              Próxima
            </Button>
          </div>
        </div>

        <CreateEditUserDialog
          open={createEditDialogOpen}
          onOpenChange={setCreateEditDialogOpen}
          user={selectedUser}
          onSave={handleSave}
        />

        <DeleteUserDialog
          user={selectedUser}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={handleConfirmDelete}
        />
      </div>
    </div>
  );
}
